import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Bike, MapPin, Package, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RiderAccept = () => {
  const { orderNumber } = useParams();
  const [sp] = useSearchParams();
  const riderId = sp.get("r") || "";
  const token = sp.get("t") || "";

  const [order, setOrder] = useState(null);
  const [state, setState] = useState("loading"); // loading | available | taken | invalid | accepting | accepted | error
  const [errorMsg, setErrorMsg] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/rider-accept/${orderNumber}`, { params: { r: riderId, t: token } });
      setOrder(r.data.order);
      if (r.data.available) setState("available");
      else setState("taken");
    } catch (e) {
      const status = e.response?.status;
      setErrorMsg(e.response?.data?.detail || "");
      if (status === 401 || status === 403) setState("invalid");
      else if (status === 404) setState("invalid");
      else setState("error");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderNumber]);

  const accept = async () => {
    setState("accepting");
    try {
      await axios.post(`${API}/rider-accept/${orderNumber}`, { rider_id: riderId, token });
      setState("accepted");
    } catch (e) {
      if (e.response?.status === 409) {
        setErrorMsg(e.response?.data?.detail || "El pedido ya está siendo gestionado por otro repartidor");
        setState("taken");
      } else {
        setErrorMsg(e.response?.data?.detail || "Error");
        setState("error");
      }
    }
  };

  return (
    <div className="min-h-screen bg-sand-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-3xl border border-navy/10 shadow-xl overflow-hidden">
        <div className="bg-navy p-5 text-white">
          <div className="text-xs uppercase tracking-widest text-white/60">Caleta Eats · Rider</div>
          <div className="font-heading text-2xl font-extrabold mt-1">Pedido #{orderNumber}</div>
        </div>

        {state === "loading" && (
          <div className="p-10 text-center text-navy/60">
            <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" />
            Cargando…
          </div>
        )}

        {state === "invalid" && (
          <div className="p-8 text-center">
            <XCircle className="w-12 h-12 text-coral mx-auto mb-3" />
            <div className="font-heading font-bold text-navy text-lg">Enlace inválido</div>
            <p className="text-sm text-navy/60 mt-1">{errorMsg || "Este enlace ya no es válido o pertenece a otro repartidor."}</p>
          </div>
        )}

        {state === "error" && (
          <div className="p-8 text-center">
            <XCircle className="w-12 h-12 text-coral mx-auto mb-3" />
            <div className="font-heading font-bold text-navy text-lg">Algo salió mal</div>
            <p className="text-sm text-navy/60 mt-1">{errorMsg || "Inténtalo de nuevo en unos segundos."}</p>
            <Button onClick={load} className="btn-coral mt-4">Reintentar</Button>
          </div>
        )}

        {state === "taken" && (
          <div className="p-8 text-center" data-testid="rider-accept-taken">
            <XCircle className="w-12 h-12 text-navy/40 mx-auto mb-3" />
            <div className="font-heading font-bold text-navy text-lg">Pedido ya asignado</div>
            <p className="text-sm text-navy/70 mt-1">
              {errorMsg || "El pedido ya está siendo gestionado por otro repartidor"}
            </p>
          </div>
        )}

        {(state === "available" || state === "accepting") && order && (
          <div className="p-5 space-y-4">
            <div className="bg-sand-50 rounded-xl p-3">
              <div className="text-xs uppercase text-navy/50">Restaurante</div>
              <div className="font-heading font-bold text-navy">{order.restaurant_name}</div>
            </div>

            <div className="bg-sand-50 rounded-xl p-3">
              <div className="text-xs uppercase text-navy/50 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Entrega</div>
              <div className="text-sm text-navy">{order.delivery?.accommodation_type} {order.delivery?.accommodation_name && `· ${order.delivery.accommodation_name}`}</div>
              {order.delivery?.room && <div className="text-sm text-navy/70">Hab.: <span className="font-semibold">{order.delivery.room}</span></div>}
              <div className="text-xs text-navy/60 mt-0.5">{order.delivery?.address}</div>
            </div>

            <div className="bg-sand-50 rounded-xl p-3">
              <div className="text-xs uppercase text-navy/50 mb-1 flex items-center gap-1"><Package className="w-3 h-3" /> Artículos</div>
              <div className="text-sm text-navy">{(order.items || []).reduce((s, i) => s + (i.qty || 1), 0)} productos · Total pedido <span className="font-heading font-bold">€{(order.total || 0).toFixed(2)}</span></div>
            </div>

            <div className="border-2 border-teal/30 bg-teal/5 rounded-xl p-3 text-center">
              <div className="text-xs uppercase text-teal font-bold">Tu ganancia</div>
              <div className="font-heading text-3xl font-extrabold text-teal mt-1" data-testid="rider-accept-payout">€{(order.rider_payout || 0).toFixed(2)}</div>
              <div className="text-[11px] text-navy/60">€2.50 fijo + 3% productos</div>
            </div>

            <Button
              onClick={accept}
              disabled={state === "accepting"}
              className="w-full h-14 rounded-full bg-coral text-white text-lg font-bold shadow-lg hover:bg-coral-hover"
              data-testid="rider-accept-btn"
            >
              {state === "accepting" ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Aceptando…</>
              ) : (
                <><Bike className="w-5 h-5 mr-2" /> Aceptar el pedido</>
              )}
            </Button>
            <p className="text-[11px] text-navy/50 text-center">El primer repartidor que acepte se lo lleva.</p>
          </div>
        )}

        {state === "accepted" && (
          <div className="p-8 text-center" data-testid="rider-accept-success">
            <CheckCircle2 className="w-14 h-14 text-teal mx-auto mb-3" />
            <div className="font-heading font-bold text-navy text-xl">¡Pedido aceptado!</div>
            <p className="text-sm text-navy/70 mt-1">Abre tu panel de repartidor para gestionar la entrega.</p>
            <a href="/rider" className="inline-block mt-4">
              <Button className="btn-coral">Abrir panel</Button>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiderAccept;
